import React from 'react';
import { FormStatus } from '@lib/state';
import {
  ErrorDisplay,
  FormFileField,
  FormTextField,
  LoadingIndicator,
  SubmitButton,
} from '@lib/common';
import { Box, Grid, Typography } from '@material-ui/core';
import { format } from 'date-fns';

interface Props {
  qrcode: { name?: string | null; location?: string | null };
  status: FormStatus;
  error?: Error;
  maintenanceTicketNumber?: number;
  notesMaxLength?: number;
}

export const MaintentanceTicketForm: React.FC<Props> = ({
  qrcode,
  status,
  error,
  maintenanceTicketNumber,
  notesMaxLength,
}) => {
  if (status !== FormStatus.Success)
    return (
      <>
        <Box display="flex" alignItems="center" justifyContent="space-between" pb={2}>
          <Typography variant="h5" paragraph>
            <strong>Report an Issue</strong>
          </Typography>
        </Box>
        <Grid container spacing={4} alignItems="center">
          {!!qrcode.name && (
            <Grid item xs={12} md={4}>
              <Typography>
                <strong>Name</strong>
              </Typography>
              <Typography>{qrcode.name}</Typography>
            </Grid>
          )}
          {!!qrcode.location && (
            <Grid item xs={12} md={4}>
              <Typography>
                <strong>Area</strong>
              </Typography>
              <Typography>{qrcode.location}</Typography>
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography>
              <strong>Issue</strong> <small>(Optional) </small>
            </Typography>
            <FormTextField
              name="notes"
              fullWidth
              multiline={true}
              inputProps={{ maxLength: notesMaxLength }}
              placeholder="Please describe the issue here."
              helperText="If immediate assistance is required, please visit the front desk."
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid item xs={9}>
            <Typography>
              <strong>Photo</strong> <small>(Optional) </small>
            </Typography>
            <FormFileField name="photo" accept="image/jpeg,image/png,image/webp" />
            <Typography variant="caption">Upload a photo</Typography>
          </Grid>
        </Grid>
        <ErrorDisplay error={error} />
        {status === FormStatus.Pending ? <LoadingIndicator loadingText="Processing..." /> : null}
        <Box display="flex" justifyContent="flex-end" pb={2}>
          <SubmitButton
            variant="contained"
            color="secondary"
            fullWidth
            pending={status === FormStatus.Pending}
            disabled={status === FormStatus.Pending}
          >
            Submit
          </SubmitButton>
        </Box>
      </>
    );

  const timestamp = format(new Date(), 'M/d/yy - h:mm a');

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h4" align="center" color="textSecondary" paragraph>
          Ticket Submitted
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" align="center" color="textSecondary" paragraph>
          Ticket #{maintenanceTicketNumber}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" align="center" color="textSecondary" paragraph>
          Submitted on {timestamp}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" align="center">
          Thank you for submitting a ticket. This will be automatically routed to our maintenance
          staff and you will receive an Email and SMS with the details of your ticket.
        </Typography>
      </Grid>
    </Grid>
  );
};
